import React, { useContext } from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { DestinationContext } from '../../../contexts/DestinationContext';
import styles from './MaraGuide.module.scss';

const routeApi = getRouteApi('/');

const WORD_LIMIT = 28;

// `description` is rich HTML from the useful-info record — strip the markup
// and cap it at a word count (rather than a paragraph or character count)
// so the card preview reads as a clean, evenly-sized snippet regardless of
// how the admin formatted the source paragraph.
const previewText = (html) => {
  if (typeof html !== 'string') return '';
  const text = html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
  const words = text.split(' ').filter(Boolean);
  return words.length > WORD_LIMIT ? `${words.slice(0, WORD_LIMIT).join(' ')}…` : text;
};

// Skips the first word (a short lead-in like "Getting" in "Getting to
// Maasai Mara") and accents everything after it, so the highlight lands
// mid-title rather than trailing off at the very end.
const renderAccentedTitle = (text) => {
  const words = (text || '').trim().split(/\s+/);
  const leadWord = words.shift();
  const restWords = words.join(' ');
  return (
    <>
      {leadWord ? `${leadWord} ` : ''}
      <span className={styles.plateTitleAccent}>{restWords}</span>
    </>
  );
};

const MaraGuide = () => {
  const destination = useContext(DestinationContext);
  const { usefulInfoArticles } = routeApi.useLoaderData();

  const selectedUsefulInfo = destination?.maraGuide?.selectedUsefulInfo;
  const sourceArticles = Array.isArray(selectedUsefulInfo) && selectedUsefulInfo.length > 0
    ? selectedUsefulInfo
    : usefulInfoArticles;

  if (!Array.isArray(sourceArticles) || sourceArticles.length === 0) return null;

  const featuredArticles = sourceArticles.slice(0, 3);

  const destinationName = destination?.name || 'Destination';

  return (
    <section className={styles.section}>
      <div className={styles.wrap}>
        <div className={styles.header}>
          <div className={styles.eyebrow}>{destinationName} Knowledge Base</div>
          <h2 className={styles.heading}>
            The {destinationName} <span className={styles.accent}>Guide</span>
          </h2>
          <p className={styles.lead}>Written by the guides who know every corner of this ecosystem.</p>
        </div>
        <div className={styles.articles}>
          {featuredArticles.map((article) => (
            <Link
              key={article._id || article.slug}
              to="/useful-info/$slug"
              params={{ slug: article.slug }}
              className={styles.article}
            >
              <div className={styles.plate}>
                <h3 className={styles.plateTitle}>
                  {renderAccentedTitle(article.eyebrow || article.title)}
                </h3>
              </div>
              <div className={styles.articleBody}>
                <p className={styles.description}>{previewText(article.description)}</p>
                <span className={styles.readLink}>Read Guide →</span>
              </div>
            </Link>
          ))}
        </div>
        <div className={styles.footer}>
          <Link to="/useful-info" className={styles.seeAll}>
            See All Guides →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default MaraGuide;
